/**
 * Card Scroll Stacking Effect
 * Creates a card stacking effect where cards scroll up and stack on top of each other
 */

import throttle from 'lodash/throttle';

// Only run on the frontend, not in the admin
if (!document.body.classList.contains('wp-admin')) {
	document.addEventListener('DOMContentLoaded', function () {
		const cardScrollBlocks =
			document.querySelectorAll('.block-card-scroll');

		cardScrollBlocks.forEach((block) => {
			const cards = block.querySelectorAll('.block-card-scroll-card');

			if (cards.length <= 1) return;

			// Configuration
			const stackGap = 25; // Pixels of previous cards that remain visible
			const stickyTop = 120; // Distance from top when cards stick
			const maxVisibleCards = 3; // Maximum number of cards visible in stack

			// Set up initial styles for cards
			cards.forEach((card, index) => {
				card.style.position = 'sticky';
				// Limit the sticky position offset to prevent excessive spacing
				const stackPosition = Math.min(index, maxVisibleCards);
				card.style.top = `${stickyTop + stackPosition * stackGap}px`;
				card.style.zIndex = index + 1; // Later cards have higher z-index (appear on top)
				card.style.transition =
					'transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)';

				// Add initial transform origin for better scaling
				card.style.transformOrigin = 'center center';
				card.style.willChange = 'transform, opacity';
			});

			// Throttled scroll handler for smooth animation
			const handleScroll = throttle(() => {
				cards.forEach((card, index) => {
					const rect = card.getBoundingClientRect();
					const cardTop = rect.top;
					const stackPosition = Math.min(index, maxVisibleCards);
					const targetTop = stickyTop + stackPosition * stackGap;

					// Check if card has reached its sticky position
					const isStuck = cardTop <= targetTop + 5;

					// Calculate overlap with next card for immediate scaling
					let overlapProgress = 0;
					if (index < cards.length - 1) {
						const nextCard = cards[index + 1];
						const nextRect = nextCard.getBoundingClientRect();
						const overlapStart = rect.bottom - 100; // Start transition 100px before overlap
						const overlapEnd = rect.top;

						if (nextRect.top < overlapStart) {
							overlapProgress = Math.max(
								0,
								Math.min(
									1,
									(overlapStart - nextRect.top) /
										(overlapStart - overlapEnd)
								)
							);
						}
					}

					if (isStuck) {
						// Count how many cards are below this one that are also stuck
						let cardsBelow = 0;
						for (let i = index + 1; i < cards.length; i++) {
							const nextRect = cards[i].getBoundingClientRect();
							const nextStackPosition = Math.min(
								i,
								maxVisibleCards
							);
							const nextTarget =
								stickyTop + nextStackPosition * stackGap;
							if (nextRect.top <= nextTarget + 5) {
								cardsBelow++;
							}
						}

						// Immediate scale based on overlap, not just stuck state
						const baseScale = 0.92;
						const scaleReduction = 0.03 * cardsBelow;
						const overlapScale = 1 - 0.08 * overlapProgress; // Scale down as overlap increases
						const finalScale = Math.max(
							baseScale - scaleReduction,
							overlapScale
						);

						const opacity = Math.max(
							0.65,
							1 - cardsBelow * 0.1 - overlapProgress * 0.15
						);

						// For cards beyond the max visible, push earlier cards up
						if (index >= maxVisibleCards && cardsBelow > 0) {
							const pushUp = -(cardsBelow * stackGap);
							card.style.transform = `translateY(${pushUp}px) scale(${finalScale})`;
							card.style.opacity = opacity;
						} else {
							card.style.transform = `scale(${finalScale})`;
							card.style.opacity = opacity;
						}
					} else if (overlapProgress > 0) {
						// Card hasn't reached sticky position yet but is being covered
						const overlapScale = 1 - 0.08 * overlapProgress;
						const overlapOpacity = 1 - overlapProgress * 0.15;
						card.style.transform = `scale(${overlapScale})`;
						card.style.opacity = overlapOpacity;
					} else {
						// Not being covered - stay at full size
						card.style.transform = 'scale(1)';
						card.style.opacity = '1';
					}
				});
			}, 16); // ~60fps

			// Intersection Observer for enabling/disabling scroll listener
			const observer = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						if (entry.isIntersecting) {
							// Card is in view, enable scroll tracking
							window.addEventListener('scroll', handleScroll);
							handleScroll(); // Initial check
						}
					});
				},
				{
					root: null,
					rootMargin: '100px',
					threshold: 0,
				}
			);

			// Observe the block container for viewport entry
			observer.observe(block);

			// Clean up on page unload
			window.addEventListener('beforeunload', () => {
				window.removeEventListener('scroll', handleScroll);
			});
		});
	});
}
